import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "./mcfUtils";

const blank = { warehouse_name: "", warehouse_code: "", address: "", city: "", state: "" };

// Drop Location = Warehouse Master entry (Code = MCF Code). City/State come from the master.
export function DropLocationField({ warehouses, value, onChange, onWarehousesChanged, isAdmin }) {
  const [fix, setFix] = useState({ city: "", state: "" });
  const [savingFix, setSavingFix] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const selected = warehouses.find((w) => w.id === value);
  const missing = selected && (!selected.city?.trim() || !selected.state?.trim());

  useEffect(() => setFix({ city: "", state: "" }), [value]);

  const options = warehouses.map((w) => ({
    value: w.id,
    label: `${w.warehouse_name} — ${w.warehouse_code}`,
    subtitle: [w.city, w.state].filter(Boolean).join(", ") || "City/State not set",
    searchTerms: [w.warehouse_code, w.city, w.state],
  }));

  const saveFix = async () => {
    setSavingFix(true);
    try {
      await axios.put(`${API}/warehouses/${selected.id}`, {
        city: fix.city.trim() || selected.city || "",
        state: fix.state.trim() || selected.state || "",
      });
      toast.success("Warehouse Master updated");
      await onWarehousesChanged();
    } catch (err) {
      toast.error(errMsg(err, "Could not update warehouse"));
    } finally {
      setSavingFix(false);
    }
  };

  const addLocation = async () => {
    if (!form.warehouse_name.trim() || !form.warehouse_code.trim() || !form.city.trim() || !form.state.trim()) {
      toast.error("Name, MCF Code, City and State are required");
      return;
    }
    try {
      const res = await axios.post(`${API}/warehouses`, { ...form, warehouse_type: "fbw_inventory", is_active: true });
      toast.success("Drop location added to Warehouse Master");
      await onWarehousesChanged();
      onChange(res.data.id);
      setAddOpen(false);
      setForm(blank);
    } catch (err) {
      toast.error(errMsg(err, "Could not add drop location"));
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Drop Location (MCF Code) *</Label>
        {isAdmin && (
          <button type="button" onClick={() => setAddOpen(true)} className="text-xs text-[#1F8F88] font-medium inline-flex items-center hover:underline" data-testid="mcf-add-drop-location">
            <Plus className="h-3 w-3 mr-0.5" /> Add location
          </button>
        )}
      </div>
      <SearchableSelect options={options} value={value} onValueChange={onChange} placeholder="Select MCF warehouse" searchPlaceholder="Search name, code, city..." data-testid="mcf-drop-location-select" />
      {missing && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm space-y-2" data-testid="mcf-drop-missing-city-state">
          <p className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            {selected.warehouse_name} ({selected.warehouse_code}) has no {!selected.city?.trim() && "City"}{!selected.city?.trim() && !selected.state?.trim() && " / "}{!selected.state?.trim() && "State"} in Warehouse Master.
            {!isAdmin && " Ask an admin to update it before saving."}
          </p>
          {isAdmin && (
            <div className="flex gap-2">
              <Input key={`city-${selected.id}`} placeholder="City" defaultValue={selected.city} onChange={(e) => setFix((f) => ({ ...f, city: e.target.value }))} className="h-8 bg-white" data-testid="mcf-fix-city-input" />
              <Input key={`state-${selected.id}`} placeholder="State" defaultValue={selected.state} onChange={(e) => setFix((f) => ({ ...f, state: e.target.value }))} className="h-8 bg-white" data-testid="mcf-fix-state-input" />
              <Button size="sm" className="h-8 bg-[#1F8F88] hover:bg-[#197a74] text-white" disabled={savingFix} onClick={saveFix} data-testid="mcf-fix-save">Save</Button>
            </div>
          )}
        </div>
      )}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Drop Location</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Saved to Control Panel → Warehouse Master as an FBW warehouse.</p>
          <div className="grid grid-cols-2 gap-3">
            {[["warehouse_name", "Warehouse Name *"], ["warehouse_code", "MCF Code *"], ["city", "City *"], ["state", "State *"]].map(([k, l]) => (
              <div key={k} className="space-y-1">
                <Label>{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`mcf-new-drop-${k}`} />
              </div>
            ))}
            <div className="col-span-2 space-y-1">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="mcf-new-drop-address" />
            </div>
          </div>
          <Button onClick={addLocation} className="bg-[#1F8F88] hover:bg-[#197a74] text-white" data-testid="mcf-new-drop-save">Add Location</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
